import { supabase } from '../lib/supabase';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://idealyze-backend.vercel.app';

// Token validation and refresh utilities
const isTokenExpired = (token) => {
  try {
    if (!token) return true;
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    // Check if token expires within the next 5 minutes (300 seconds)
    return payload.exp <= (currentTime + 300);
  } catch (error) {
    console.error('Error parsing token:', error);
    return true;
  }
};

const refreshToken = async () => {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      console.error('Token refresh error:', error);
      return null;
    }
    if (data?.session?.access_token) {
      localStorage.setItem('token', data.session.access_token);
      console.log('Token refreshed successfully');
      return data.session.access_token;
    }
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
};

const getValidToken = async (token) => {
  if (!token) {
    console.warn('No token provided');
    return null;
  }

  // If token is still valid, return it
  if (!isTokenExpired(token)) {
    return token;
  }

  console.log('Token expired, attempting to refresh...');

  // Try to refresh the token
  const newToken = await refreshToken();
  if (newToken) {
    return newToken;
  }

  // If refresh failed, clear invalid token and redirect to login
  console.warn('Token refresh failed, clearing invalid token');
  localStorage.removeItem('token');
  localStorage.removeItem('user');

  // Redirect to login page
  if (window.location.pathname !== '/auth') {
    window.location.href = '/auth';
  }

  return null;
};

export const apiClient = {
  // AI generation calls to your backend
  generateContent: async (type, ideaDescription, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/ai/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify({ type, ideaDescription })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to generate content')
    }

    return response.json()
  },

  // Get user analytics from backend
  getUserAnalytics: async (token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/analytics`, {
      headers: {
        'Authorization': `Bearer ${validToken}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch analytics')
    }

    return response.json()
  },

  // Get dashboard data
  getDashboardData: async (token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/dashboard`, {
      headers: {
        'Authorization': `Bearer ${validToken}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch dashboard data')
    }

    return response.json()
  },

  // Record idea view
  recordIdeaView: async (ideaId, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/interactions/view`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify({ ideaId })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to record view')
    }

    return response.json()
  },

  // Toggle bookmark
  toggleBookmark: async (ideaId, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/interactions/bookmark`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify({ ideaId })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to toggle bookmark')
    }

    return response.json()
  },

  // Toggle like
  toggleLike: async (ideaId, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/interactions/like`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify({ ideaId })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to toggle like')
    }

    return response.json()
  },

  // Toggle dislike
  toggleDislike: async (ideaId, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/interactions/dislike`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify({ ideaId })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to toggle dislike')
    }

    return response.json()
  },

  // Record AI interaction
  recordAIInteraction: async (ideaId, featureType, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/ai/interaction`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify({ ideaId, featureType })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to record AI interaction')
    }

    return response.json()
  },

  // Record "Build this idea" button click
  recordBuildClick: async (ideaId, token) => {
    console.log('Making build click API call to:', `${API_BASE_URL}/api/interactions/build-click`)
    // Preparing build click request
    
    const response = await fetch(`${API_BASE_URL}/api/interactions/build-click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ ideaId })
    })
    
    console.log('Build click API response status:', response.status)
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error('Build click API error response:', errorText)
      throw new Error(errorText || 'Failed to record build click')
    }
    
    const result = await response.json()
    // Build click recorded successfully
    return result
  },

  // Get user bookmarks
  getUserBookmarks: async (token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/interactions/bookmarks`, {
      headers: {
        'Authorization': `Bearer ${validToken}`
      }
    })

    if (!response.ok) {
      let errorMessage = 'Failed to fetch bookmarks'
      try {
        const responseText = await response.text()
        try {
          const error = JSON.parse(responseText)
          errorMessage = error.message || errorMessage
        } catch (parseError) {
          // If JSON parsing fails, use the response text or status
          errorMessage = responseText || `HTTP ${response.status}: ${response.statusText}`
        }
      } catch (textError) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`
      }
      throw new Error(errorMessage)
    }

    return response.json()
  },

  // Get all ideas with filtering and pagination (now using Supabase directly)
  getIdeas: async (filters = {}, page = 1, limit = 10, language = 'en', searchQuery = '') => {
    try {
      // First, get the total count
      let countQuery = supabase
        .from('ideas')
        .select('*', { count: 'exact', head: true })

      // Apply search query for count
      if (searchQuery && searchQuery.trim()) {
        countQuery = countQuery.or(`title.ilike.%${searchQuery.trim()}%,title_ar.ilike.%${searchQuery.trim()}%,description.ilike.%${searchQuery.trim()}%,description_ar.ilike.%${searchQuery.trim()}%`)
      }

      // Apply the same filters for count
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all') {
          countQuery = countQuery.eq(key, filters[key])
        }
      })

      const { count, error: countError } = await countQuery

      if (countError) {
        throw new Error(countError.message)
      }

      // Then get the actual data
      let query = supabase
        .from('ideas')
        .select(`
          *,
          idea_competitors(*),
          idea_financial_data(*),
          idea_technical_requirements(*)
        `)

      // Apply search query
      if (searchQuery && searchQuery.trim()) {
        query = query.or(`title.ilike.%${searchQuery.trim()}%,title_ar.ilike.%${searchQuery.trim()}%,description.ilike.%${searchQuery.trim()}%,description_ar.ilike.%${searchQuery.trim()}%`)
      }

      // Apply filters
      Object.keys(filters).forEach(key => {
        if (filters[key] && filters[key] !== 'all') {
          query = query.eq(key, filters[key])
        }
      })
      
      // Apply pagination
      const offset = (page - 1) * limit
      query = query.range(offset, offset + limit - 1)
      
      // Order by created_at desc to get newest first
      query = query.order('created_at', { ascending: false })
      
      const { data: ideas, error } = await query
      
      if (error) {
        throw new Error(error.message)
      }
      
      // Transform ideas to include language-specific content
      const transformedIdeas = ideas.map(idea => ({
        ...idea,
        // Use Arabic content if available and language is Arabic, otherwise use English
        title: language === 'ar' && idea.title_ar ? idea.title_ar : idea.title,
        description: language === 'ar' && idea.description_ar ? idea.description_ar : idea.description,
        why_now: language === 'ar' && idea.why_now_ar ? idea.why_now_ar : idea.why_now,
        revenue_model: language === 'ar' && idea.revenue_model_ar ? idea.revenue_model_ar : idea.revenue_model,
        target_audience: language === 'ar' && idea.target_audience_ar ? idea.target_audience_ar : idea.target_audience,
        go_to_market: language === 'ar' && idea.go_to_market_ar ? idea.go_to_market_ar : idea.go_to_market,
        // Keep original English values for filtering purposes
        title_en: idea.title,
        description_en: idea.description,
        // Add flag to indicate if Arabic translation is available
        hasArabicTranslation: !!(idea.title_ar && idea.description_ar)
      }))
      
      return { 
        ideas: transformedIdeas, 
        totalCount: count,
        totalPages: Math.ceil(count / limit),
        currentPage: page,
        hasMore: offset + limit < count
      }
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch ideas')
    }
  },

  // Get idea risks by ID
  getIdeaRisks: async (ideaId, language = 'en') => {
    try {
      const { data: risks, error } = await supabase
        .from('idea_risks')
        .select('*')
        .eq('idea_id', ideaId)
      
      if (error) throw error
      
      // Group risks by category
      const riskCategories = ['Financial', 'Market', 'Technical', 'Operational', 'Regulatory']
      const groupedRisks = {}
      
      riskCategories.forEach(category => {
        groupedRisks[category] = risks.filter(risk => risk.risk_category === category)
      })
      
      return groupedRisks
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch idea risks')
    }
  },

  // Get single idea by ID with all related data (now using Supabase directly)
  getIdeaById: async (id, language = 'en') => {
    try {
      const { data: idea, error } = await supabase
        .from('ideas')
        .select(`
          *,
          idea_competitors(*),
          idea_financial_data(*),
          idea_technical_requirements(*),
          idea_financial_projections(*),
          idea_implementation_phases(*),
          idea_marketing_channels(*),
          idea_positioning_strategies(*),
          idea_pricing_plans(*),
          idea_risks(*)
        `)
        .eq('id', id)
        .single()
      
      if (error) {
        throw new Error(error.message)
      }
      
      if (!idea) {
        throw new Error('Idea not found')
      }
      
      // Transform idea to include language-specific content for ALL related data
      const transformedIdea = {
        ...idea,
        // Main idea translations
        title: language === 'ar' && idea.title_ar ? idea.title_ar : idea.title,
        description: language === 'ar' && idea.description_ar ? idea.description_ar : idea.description,
        why_now: language === 'ar' && idea.why_now_ar ? idea.why_now_ar : idea.why_now,
        revenue_model: language === 'ar' && idea.revenue_model_ar ? idea.revenue_model_ar : idea.revenue_model,
        target_audience: language === 'ar' && idea.target_audience_ar ? idea.target_audience_ar : idea.target_audience,
        go_to_market: language === 'ar' && idea.go_to_market_ar ? idea.go_to_market_ar : idea.go_to_market,
        
        // Transform financial projections with language support
        idea_financial_projections: idea.idea_financial_projections?.map(proj => ({
          ...proj,
          financial_estimation_summary: language === 'ar' && proj.financial_estimation_summary_ar 
            ? proj.financial_estimation_summary_ar 
            : proj.financial_estimation_summary
        })) || [],
        
        // Transform implementation phases with language support
        idea_implementation_phases: idea.idea_implementation_phases?.map(phase => ({
          ...phase,
          key_milestones: language === 'ar' && phase.key_milestones_ar 
            ? phase.key_milestones_ar 
            : phase.key_milestones,
          required_resources: language === 'ar' && phase.required_resources_ar 
            ? phase.required_resources_ar 
            : phase.required_resources
        })) || [],
        
        // Transform marketing channels with language support
        idea_marketing_channels: idea.idea_marketing_channels?.map(channel => ({
          ...channel,
          tactics: language === 'ar' && channel.tactics_ar 
            ? channel.tactics_ar 
            : channel.tactics
        })) || [],
        
        // Transform positioning strategies with language support
        idea_positioning_strategies: idea.idea_positioning_strategies?.map(strategy => ({
          ...strategy,
          positioning_angle: language === 'ar' && strategy.positioning_angle_ar 
            ? strategy.positioning_angle_ar 
            : strategy.positioning_angle,
          value_proposition: language === 'ar' && strategy.value_proposition_ar 
            ? strategy.value_proposition_ar 
            : strategy.value_proposition,
          competitive_advantage: language === 'ar' && strategy.competitive_advantage_ar 
            ? strategy.competitive_advantage_ar 
            : strategy.competitive_advantage,
          target_segment: language === 'ar' && strategy.target_segment_ar 
            ? strategy.target_segment_ar 
            : strategy.target_segment,
          messaging: language === 'ar' && strategy.messaging_ar 
            ? strategy.messaging_ar 
            : strategy.messaging
        })) || [],
        
        // Transform pricing plans with language support
        idea_pricing_plans: idea.idea_pricing_plans?.map(plan => ({
          ...plan,
          plan_name: language === 'ar' && plan.plan_name_ar 
            ? plan.plan_name_ar 
            : plan.plan_name,
          description: language === 'ar' && plan.description_ar 
            ? plan.description_ar 
            : plan.description
        })) || [],
        
        // Transform risks with language support
        idea_risks: idea.idea_risks?.map(risk => ({
          ...risk,
          risk_description: language === 'ar' && risk.risk_description_ar 
            ? risk.risk_description_ar 
            : risk.risk_description,
          mitigation_strategy: language === 'ar' && risk.mitigation_strategy_ar 
            ? risk.mitigation_strategy_ar 
            : risk.mitigation_strategy
        })) || [],
        
        // Transform competitors with language support
        idea_competitors: idea.idea_competitors?.map(competitor => ({
          ...competitor,
          strengths: language === 'ar' && competitor.strengths_ar 
            ? competitor.strengths_ar 
            : competitor.strengths,
          weaknesses: language === 'ar' && competitor.weaknesses_ar 
            ? competitor.weaknesses_ar 
            : competitor.weaknesses
        })) || [],
        
        // Keep original English values
        title_en: idea.title,
        description_en: idea.description,
        // Add flag to indicate if Arabic translation is available
        hasArabicTranslation: !!(idea.title_ar && idea.description_ar)
      }
      
      return transformedIdea
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch idea')
    }
  },

  // Get ideas analytics summary
  getIdeasSummary: async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/ideas/analytics/summary`, {
      headers: { 
        'Authorization': `Bearer ${token}` 
      }
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch ideas summary')
    }
    
    return response.json()
  },

  // Change password
  changePassword: async (currentPassword, newPassword, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to change password')
    }

    return response.json()
  },

  // Get current user stats
  getUserStats: async (token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/stats`, {
      headers: {
        'Authorization': `Bearer ${validToken}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to fetch user stats')
    }

    return response.json()
  },

  // Billing functions for Stripe integration
  createCheckoutSession: async (token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/billing/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      }
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to create checkout session')
    }

    return response.json()
  },

  // Create Idealizer checkout session - one-time payment
  createIdealizerCheckoutSession: async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/billing/create-idealizer-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to create Idealizer checkout session')
    }
    
    return response.json()
  },

  createPortalSession: async (token, sessionId = null) => {
    const response = await fetch(`${API_BASE_URL}/api/billing/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(sessionId ? { session_id: sessionId } : {})
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to create portal session')
    }
    
    return response.json()
  },

  // Cancel subscription - stops future billing
  cancelSubscription: async (token, cancelAtPeriodEnd = true) => {
    const response = await fetch(`${API_BASE_URL}/api/billing/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ cancelAtPeriodEnd })
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to cancel subscription')
    }
    
    return response.json()
  },

  // Reactivate subscription - undoes cancellation
  reactivateSubscription: async (token) => {
    const response = await fetch(`${API_BASE_URL}/api/billing/reactivate-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Failed to reactivate subscription')
    }
    
    return response.json()
  },

  // AI Chat Stream for BuildersStudio
  aiChatStream: async (message, conversationHistory, language, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/ai/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify({
        message,
        conversationHistory,
        language
      })
    });

    if (!response.ok) {
      throw new Error('Failed to get response from AI chat');
    }

    return response;
  },

  // Generate Logo with Imagen
  generateLogo: async (selectedConcept, language, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/ai/imagen/generate-logo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify({
        selectedConcept,
        language
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to generate logo');
    }

    return response.json();
  },

  // Generate Startup Ideas
  generateStartupIdeas: async (formData, token) => {
    const validToken = await getValidToken(token);
    if (!validToken) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_BASE_URL}/api/ai/generate-startup-ideas`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${validToken}`
      },
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to generate startup ideas');
    }

    return response.json();
  }
}
